import db from "../db/database";
import { paraComparar } from "../config/menu/buscadorDelMenu";

/**
 * Buscar en todo el terminal desde la cabecera.
 *
 * Escribir «prueba» y que salgan el cliente Prueba y la tienda Prueba, sin
 * saber de antemano en qué maestro estaba ni tener que abrir cada pantalla a
 * mirar. Es la pregunta «¿dónde está esto?», que hoy sólo se puede contestar
 * pantalla por pantalla.
 *
 * ## Qué se mira de cada ficha
 *
 * El **nombre y el código**, no todos los campos. Buscar dentro de la dirección
 * o de las notas encuentra más, pero también encuentra cosas que nadie pidió:
 * un cliente cuya calle se llama igual que el producto buscado sale mezclado
 * con el producto, y el resultado deja de ser fiable. Acá se busca *lo que algo
 * se llama*.
 *
 * ## Por qué no se pagina ni se ordena por relevancia
 *
 * Cada fuente devuelve unas pocas y se dice cuántas había. Un desplegable de
 * cabecera no es una pantalla de resultados: si hay treinta clientes que
 * coinciden, lo útil no es enseñar los treinta sino llevar a Clientes con la
 * búsqueda puesta, que es lo que hace el enlace del final de cada grupo.
 */

/**
 * De dónde se busca, en el orden en que se muestra.
 *
 * Los nombres de campo salen del esquema real de cada tabla --no se parecen
 * entre sí: `name` en clientes, `description` en productos, `nombre` en
 * tiendas, `label` en vendedores-- así que cada fuente dice los suyos. Se
 * prueban en orden y gana el primero que traiga algo.
 */
export const FUENTES = [
  { tabla: "clients", tipo: "Cliente", ruta: "/clientes", titulo: ["name"], codigo: ["No"] },
  { tabla: "products", tipo: "Producto", ruta: "/productos", titulo: ["description"], codigo: ["No", "barcode"] },
  { tabla: "tpvs", tipo: "Tienda", ruta: "/tpvs", titulo: ["nombre"], codigo: ["No"] },
  { tabla: "cajas", tipo: "Caja", ruta: "/cajas", titulo: ["nombre"], codigo: ["No"] },
  { tabla: "colegios", tipo: "Colegio", ruta: "/colegios", titulo: ["nombre"], codigo: ["No"] },
  { tabla: "users", tipo: "Usuario", ruta: "/usuarios", titulo: ["username", "email"], codigo: ["No"] },
  { tabla: "vendedores", tipo: "Vendedor", ruta: "/vendedores", titulo: ["label"], codigo: ["value"] },
  { tabla: "turnos", tipo: "Turno", ruta: "/turnos", titulo: ["DescripcionTurno"], codigo: ["No"] },
  { tabla: "formasPago", tipo: "Forma de pago", ruta: "/formas-pago", titulo: ["Description"], codigo: ["Code", "No"] },
  { tabla: "coupons", tipo: "Cupón", ruta: "/cupones", titulo: ["Description"], codigo: ["No"] },
  { tabla: "campaigns", tipo: "Campaña", ruta: "/campanas", titulo: ["Description"], codigo: ["No"] },
  // Los documentos se buscan también por los números que salen **en el papel**:
  // el comprobante fiscal y, en Costa Rica, la clave de Hacienda. Quien llega al
  // mostrador con un ticket en la mano tiene ésos, no el número interno --y
  // buscando por lo que traía escrito no encontraba nada--. La clave son 50
  // dígitos y se busca por fragmento, que es como se leen de un papel.
  {
    tabla: "invoices",
    tipo: "Documento",
    ruta: "/facturas",
    titulo: ["Bill_to_Name"],
    codigo: ["No", "Pre_Assigned_No", "No_Comprobante_Fiscal", "Clave"],
  },
];

const primero = (registro, campos) => {
  for (const campo of campos) {
    const valor = String(registro?.[campo] ?? "").trim();
    if (valor) return valor;
  }
  return "";
};

/** Si una ficha responde a lo buscado, por su nombre o por su código. */
export const fichaCoincide = (registro, fuente, buscado) => {
  if (!registro || registro.deleted) return false;
  const campos = [...fuente.titulo, ...fuente.codigo];
  return campos.some((campo) => paraComparar(registro[campo]).includes(buscado));
};

/**
 * @param {string} texto Lo escrito en la cabecera.
 * @param {{porFuente?: number, minimo?: number}} [opciones]
 * @returns {Promise<Array<{tipo: string, ruta: string, total: number, fichas: Array<{titulo: string, codigo: string}>}>>}
 */
export const buscarEnTodo = async (texto, { porFuente = 5, minimo = 2 } = {}) => {
  const buscado = paraComparar(texto);
  // Con una sola letra coincide medio catálogo y el desplegable deja de servir.
  if (buscado.length < minimo) return [];

  const grupos = await Promise.all(
    FUENTES.map(async (fuente) => {
      try {
        const tabla = db[fuente.tabla];
        if (!tabla) return null;

        const encontradas = await tabla.filter((r) => fichaCoincide(r, fuente, buscado)).toArray();
        if (!encontradas.length) return null;

        return {
          tipo: fuente.tipo,
          ruta: fuente.ruta,
          total: encontradas.length,
          fichas: encontradas.slice(0, porFuente).map((r) => ({
            titulo: primero(r, fuente.titulo) || primero(r, fuente.codigo),
            codigo: primero(r, fuente.codigo),
          })),
        };
      } catch (error) {
        // Una tabla que falla no puede llevarse por delante la búsqueda entera:
        // se pierde ese grupo y los demás siguen.
        console.error(`Error al buscar en ${fuente.tabla}:`, error);
        return null;
      }
    })
  );

  return grupos.filter(Boolean);
};
